import React, { useState, useEffect, useCallback } from "react";
import ListView from "../../../../components/common/ListView";
import MultiSelectInput from "../../../../components/common/MultiSelectInput";
import InputField from "../../../../components/common/InputField";
import {
  IconSearch,
  IconPlus,
  IconTrash,
  IconPencil,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import {
  fetchHrShiftTypes,
  createHrShiftType,
  updateHrShiftType,
  deleteHrShiftType,
  fetchHrAttendanceBreaks,
} from "../../../../apis/hrApi";
import toast from "react-hot-toast";

const DAY_OPTIONS = [
  { value: "MONDAY", text: "Monday" },
  { value: "TUESDAY", text: "Tuesday" },
  { value: "WEDNESDAY", text: "Wednesday" },
  { value: "THURSDAY", text: "Thursday" },
  { value: "FRIDAY", text: "Friday" },
  { value: "SATURDAY", text: "Saturday" },
  { value: "SUNDAY", text: "Sunday" },
];

export default function EmployeeShiftTab() {
  const [shiftData, setShiftData] = useState<any[]>([]);
  const [breakOptions, setBreakOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);

  // Sidebar State
  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "DELETE" | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // Form Fields State
  const [shiftName, setShiftName] = useState("");
  const [shiftCode, setShiftCode] = useState("");
  const [breakIds, setBreakIds] = useState<string[]>([]);
  const [totalBreakDuration, setTotalBreakDuration] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [halfTime, setHalfTime] = useState("");
  const [earlyClockInMinutes, setEarlyClockInMinutes] = useState("");
  const [lateMarkAfterMinutes, setLateMarkAfterMinutes] = useState("");
  const [maxCheckIn, setMaxCheckIn] = useState("");
  const [officeOpenDays, setOfficeOpenDays] = useState<string[]>([]);
  const [color, setColor] = useState("#4CAF50");
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load Shift Types
  const loadShifts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHrShiftTypes(currentPage, limit, searchQuery);
      if (res.success) {
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.shiftTypes || res.data.list || res.data.data || res.data.shiftType;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        const mapped = list.map((s: any) => ({
          ...s,
          shiftName: s.shiftName || s.typeName || "",
          shiftCode: s.shiftCode || s.typeCode || "",
        }));
        setShiftData(mapped);
        const total = res.data?.total || res.data?.totalCount || list.length;
        setTotalRecords(total);
        setTotalPages(res.data?.totalPages || Math.ceil(total / limit) || 1);
      } else {
        setError("Failed to load shift types");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load shift types");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, searchQuery]);

  // Load Attendance Breaks options
  const loadBreaks = useCallback(async () => {
    try {
      const res = await fetchHrAttendanceBreaks(1, 100, "");
      if (res.success) {
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.attendanceBreaks || res.data.list || res.data.data || res.data.attendanceBreak;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        const formatted = list.map((b: any) => ({
          id: b.id || b._id,
          value: b.id || b._id,
          text: `${b.title} (${b.breakTimeMinutes} mins)`,
        }));
        setBreakOptions(formatted);
      }
    } catch (err) {
      console.error("Failed to load breaks", err);
    }
  }, []);

  useEffect(() => {
    loadShifts();
    loadBreaks();
  }, [loadShifts, loadBreaks]);

  const handleToggleDefault = async (item: any) => {
    if (item.isDefault) return; // Already default
    try {
      const payload = {
        typeName: item.typeName || item.shiftName || "",
        typeCode: item.typeCode || item.shiftCode || "",
        breakIds: item.breakIds || [],
        totalBreakDuration: item.totalBreakDuration || 0,
        startTime: item.startTime,
        endTime: item.endTime,
        halfTime: item.halfTime,
        earlyClockInMinutes: item.earlyClockInMinutes || 0,
        lateMarkAfterMinutes: item.lateMarkAfterMinutes || 0,
        maxCheckIn: item.maxCheckIn || 0,
        officeOpenDays: item.officeOpenDays || [],
        color: item.color || "#4CAF50",
        isDefault: true,
        status: item.status || "ACTIVE",
      };
      const res = await updateHrShiftType(item.id || item._id, payload);
      if (res.success) {
        toast.success(`Set ${item.shiftName} as default shift`);
        loadShifts();
      } else {
        toast.error(res.message || "Failed to set default shift");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to set default shift");
    }
  };

  // Open sidebar forms
  const handleOpenAdd = () => {
    setShiftName("");
    setShiftCode("");
    setBreakIds([]);
    setTotalBreakDuration("");
    setStartTime("");
    setEndTime("");
    setHalfTime("");
    setEarlyClockInMinutes("");
    setLateMarkAfterMinutes("");
    setMaxCheckIn("");
    setOfficeOpenDays([]);
    setColor("#4CAF50");
    setIsDefault(false);
    setSelectedItem(null);
    setSidebarMode("ADD");
  };

  const handleOpenEdit = (item: any) => {
    setShiftName(item.shiftName || "");
    setShiftCode(item.shiftCode || "");
    setBreakIds(item.breakIds || []);
    setTotalBreakDuration(item.totalBreakDuration?.toString() || "");
    setStartTime(item.startTime || "");
    setEndTime(item.endTime || "");
    setHalfTime(item.halfTime || "");
    setEarlyClockInMinutes(item.earlyClockInMinutes?.toString() || "");
    setLateMarkAfterMinutes(item.lateMarkAfterMinutes?.toString() || "");
    setMaxCheckIn(item.maxCheckIn?.toString() || "");
    setOfficeOpenDays(item.officeOpenDays || []);
    setColor(item.color || "#4CAF50");
    setIsDefault(!!item.isDefault);
    setSelectedItem(item);
    setSidebarMode("EDIT");
  };

  const handleOpenDelete = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("DELETE");
  };

  // Submit Operations
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftName.trim()) {
      toast.error("Please enter a Shift Name.");
      return;
    }
    if (!shiftCode.trim()) {
      toast.error("Please enter a Shift Code.");
      return;
    }
    if (!startTime) {
      toast.error("Please select a Start Time.");
      return;
    }
    if (!endTime) {
      toast.error("Please select an End Time.");
      return;
    }

    setIsSubmitting(true);
    
    // Ensure seconds are included if not present in native time outputs (HH:MM vs HH:MM:SS)
    const formatTimeForBackend = (t: string) => {
      if (!t) return "";
      return t.split(":").length === 2 ? `${t}:00` : t;
    };

    const payload = {
      typeName: shiftName.trim(),
      typeCode: shiftCode.trim(),
      breakIds,
      totalBreakDuration: totalBreakDuration ? parseInt(totalBreakDuration, 10) : 0,
      startTime: formatTimeForBackend(startTime),
      endTime: formatTimeForBackend(endTime),
      halfTime: formatTimeForBackend(halfTime),
      earlyClockInMinutes: earlyClockInMinutes ? parseInt(earlyClockInMinutes, 10) : 0,
      lateMarkAfterMinutes: lateMarkAfterMinutes ? parseInt(lateMarkAfterMinutes, 10) : 0,
      maxCheckIn: maxCheckIn ? parseInt(maxCheckIn, 10) : 0,
      officeOpenDays,
      color,
      isDefault,
      status: "ACTIVE",
    };

    try {
      let res;
      if (sidebarMode === "EDIT" && selectedItem) {
        res = await updateHrShiftType(selectedItem.id || selectedItem._id, payload);
      } else {
        res = await createHrShiftType(payload);
      }

      if (res.success) {
        toast.success(
          sidebarMode === "EDIT"
            ? "Shift type updated successfully"
            : "Shift type created successfully"
        );
        loadShifts();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to save shift type");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while saving shift type");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const id = selectedItem.id || selectedItem._id;
      const res = await deleteHrShiftType(id);
      if (res.success) {
        toast.success("Shift type deleted successfully");
        loadShifts();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to delete shift type");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete shift type");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Utilities for formatting times and days
  const formatTime12h = (timeStr: string) => {
    if (!timeStr) return "-";
    const parts = timeStr.split(":");
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = hours < 10 ? `0${hours}` : hours;
    return `${hoursStr}:${minutes} ${ampm}`;
  };

  const renderOfficeOpenDays = (days: string[]) => {
    if (!days || days.length === 0) return "-";
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    const displayDays = days.map(capitalize);
    return (
      <div className="text-xs">
        <span className="font-semibold text-gray-800">{displayDays[0]}</span>
        {displayDays.length > 1 && (
          <div className="text-blue-500 font-semibold mt-0.5">
            (+{displayDays.length - 1} more)
          </div>
        )}
      </div>
    );
  };

  // ListView Columns Definition
  const columns = [
    {
      label: "NAME",
      render: (s: any) => (
        <span
          className="px-3 py-1.5 rounded-lg text-white font-bold text-xs inline-block shadow-sm"
          style={{ backgroundColor: s.color || "#4CAF50" }}
        >
          {s.shiftName}
        </span>
      ),
    },
    {
      label: "START TIME",
      render: (s: any) => <span className="text-sm font-semibold text-gray-700">{formatTime12h(s.startTime)}</span>,
    },
    {
      label: "END TIME",
      render: (s: any) => <span className="text-sm font-semibold text-gray-700">{formatTime12h(s.endTime)}</span>,
    },
    {
      label: "OTHER DETAIL",
      render: (s: any) => (
        <div className="text-[11px] space-y-0.5 text-gray-600 font-medium leading-relaxed">
          <div>Late Mark After: <span className="font-semibold text-gray-800">{s.lateMarkAfterMinutes ?? "-"}</span></div>
          <div>Max Check In: <span className="font-semibold text-gray-800">{s.maxCheckIn ?? "-"}</span></div>
          <div>Early Clock In: <span className="font-semibold text-gray-800">{s.earlyClockInMinutes ?? "-"}</span></div>
        </div>
      ),
    },
    {
      label: "OFFICE OPENS ON",
      render: (s: any) => renderOfficeOpenDays(s.officeOpenDays),
    },
    {
      label: "HALF TIME",
      render: (s: any) => <span className="text-sm font-semibold text-gray-700">{formatTime12h(s.halfTime)}</span>,
    },
    {
      label: "DEFAULT",
      render: (s: any) => (
        <button
          type="button"
          onClick={() => handleToggleDefault(s)}
          className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all cursor-pointer ${
            s.isDefault ? "border-black bg-white" : "border-gray-300 hover:border-gray-400"
          }`}
        >
          {s.isDefault && <span className="w-2 h-2 rounded-full bg-black" />}
        </button>
      ),
    },
    {
      label: "ACTIONS",
      render: (s: any) => (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => handleOpenEdit(s)}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
            title="Edit"
          >
            <IconPencil size={15} stroke={1.5} />
          </button>
          <button
            onClick={() => handleOpenDelete(s)}
            className="w-8 h-8 rounded-full border border-red-100 flex items-center justify-center hover:bg-red-50 transition text-red-500 cursor-pointer bg-white"
            title="Delete"
          >
            <IconTrash size={15} stroke={1.5} />
          </button>
        </div>
      ),
    },
  ];

  const isSidebarOpen = sidebarMode !== null;

  // Filter columns dynamically to fit when the right sidebar is open
  const activeColumns = isSidebarOpen
    ? columns.filter((_, idx) => [0, 1, 2, 6, 7].includes(idx)) // Show NAME, START, END, DEFAULT, ACTIONS
    : columns;

  const sidebarContent = sidebarMode ? (
    <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden animate-in slide-in-from-right duration-200">
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
        <h2 className="text-lg font-bold text-[#212121]">
          {sidebarMode === "ADD"
            ? "Add Shift Type"
            : sidebarMode === "EDIT"
            ? "Edit Shift Type"
            : "Delete Shift"}
        </h2>
        <button
          type="button"
          onClick={() => {
            setSidebarMode(null);
            setSelectedItem(null);
          }}
          className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
          title="Close"
        >
          <IconX size={18} />
        </button>
      </div>

      {sidebarMode === "DELETE" ? (
        <div className="p-6 bg-white space-y-6">
          <p className="text-sm text-gray-600 font-medium">
            Are you sure you want to delete this shift type?
          </p>
          {selectedItem && (
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">NAME</span>
              <span className="text-sm font-semibold text-gray-800">{selectedItem.shiftName}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white p-6 max-h-[calc(100vh-160px)] overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <InputField
              label="Shift Name *"
              placeholder="Ex. Morning Shift"
              value={shiftName}
              onChange={(e) => setShiftName(e.target.value)}
              required
            />
            <InputField
              label="Shift Code *"
              placeholder="Ex. MOR"
              value={shiftCode}
              onChange={(e) => setShiftCode(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <MultiSelectInput
              name="breaks"
              label="Breaks"
              placeholder="Select A Break"
              selectOptions={breakOptions}
              value={breakIds}
              onChange={setBreakIds}
            />
            <InputField
              label="Total Break Duration (min)"
              placeholder="0"
              type="number"
              value={totalBreakDuration}
              onChange={(e) => setTotalBreakDuration(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <InputField
              label="Start Time *"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
            <InputField
              label="End Time *"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <InputField
              label="Half Time *"
              type="time"
              value={halfTime}
              onChange={(e) => setHalfTime(e.target.value)}
              required
            />
            <InputField
              label="Early Clock In (min) *"
              type="number"
              placeholder="Ex. 30"
              value={earlyClockInMinutes}
              onChange={(e) => setEarlyClockInMinutes(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <InputField
              label="Late Mark After (min) *"
              type="number"
              placeholder="Ex. 15"
              value={lateMarkAfterMinutes}
              onChange={(e) => setLateMarkAfterMinutes(e.target.value)}
              required
            />
            <InputField
              label="Max Check-in"
              type="number"
              placeholder="Ex. 3"
              value={maxCheckIn}
              onChange={(e) => setMaxCheckIn(e.target.value)}
            />
          </div>

          <div>
            <MultiSelectInput
              name="officeOpenDays"
              label="Office Opens On *"
              placeholder="Select Day..."
              selectOptions={DAY_OPTIONS}
              value={officeOpenDays}
              onChange={setOfficeOpenDays}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4 items-center">
            <div className="space-y-1">
              <label className="text-[#1E293B] text-xs font-semibold block">Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="px-3 py-1.5 text-xs w-24 rounded-xl outline-none bg-white border border-gray-200"
                  placeholder="#4CAF50"
                />
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-8 h-8 rounded-lg cursor-pointer border-none bg-transparent flex-shrink-0"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 sm:mt-5 xl:mt-4 select-none">
              <input
                type="checkbox"
                id="isDefaultCheckbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="accent-[#FBD037] w-4 h-4 rounded cursor-pointer"
              />
              <label htmlFor="isDefaultCheckbox" className="text-[#1E293B] text-xs font-semibold cursor-pointer">
                Is Default Shift
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : null}
              <span>{sidebarMode === "EDIT" ? "Update Shift" : "Create Shift"}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  ) : null;

  return (
    <div className="w-full">
      <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-r-3xl rounded-bl-3xl w-full mt-2 animate-in fade-in duration-300 items-start">
        <div className={isSidebarOpen ? "col-span-12 xl:col-span-7 transition-all duration-300" : "col-span-12"}>
          <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden flex flex-col justify-start">
            <div className="px-6 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100">
              <h2 className="text-lg font-medium text-[#212121]">Employee Shift</h2>
              <div className="flex items-center flex-wrap gap-2.5 justify-start sm:justify-end">
                {/* Search input slider */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-48 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none animate-in fade-in duration-200"
                        />
                        {searchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Refresh button */}
                <button
                  onClick={loadShifts}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  title="Refresh"
                >
                  <IconRefresh size={17} stroke={2.5} />
                </button>

                {/* Add Shift button */}
                <button
                  onClick={handleOpenAdd}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#e2bd30] border border-[#FBD037] text-[#212121] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                  title="Add Shift Type"
                >
                  <IconPlus stroke={2.5} size={17} />
                </button>
              </div>
            </div>

            <div className="pb-2">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center">
                  <svg
                    className="animate-spin h-6 w-6 text-[#FBD037]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
              ) : shiftData.length === 0 ? (
                <div className="p-8 text-center text-gray-400 font-medium">No Shift Types Found</div>
              ) : (
                <>
                  <ListView
                    mainDivClassName="bg-transparent"
                    tdClassName="rounded-r-none rounded-l-none"
                    trClassName="hover:bg-[#FBD037]/20 transition-colors border-b border-gray-100"
                    mailDivClassName="p-0 bg-transparent"
                    thClassName="border-t"
                    columns={activeColumns}
                    data={shiftData}
                  />
                  <div className="flex justify-end px-6 py-4 text-sm text-gray-500 items-center gap-2">
                    <span>
                      Record {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalRecords)} of{" "}
                      {totalRecords}
                    </span>
                    <span className="mx-2">{limit}</span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="opacity-50 disabled:opacity-20 cursor-pointer"
                    >
                      &lt;
                    </button>
                    <span>
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="opacity-50 disabled:opacity-20 cursor-pointer"
                    >
                      &gt;
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Sidebar (static column inside grid) */}
        {isSidebarOpen && (
          <div className="hidden xl:block xl:col-span-5 space-y-4 mt-2">
            {sidebarContent}
          </div>
        )}
      </div>

      {/* Mobile/Tablet Sidebar (fixed overlay outside grid) */}
      {isSidebarOpen && (
        <div className="xl:hidden">
          <div
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => {
              setSidebarMode(null);
              setSelectedItem(null);
            }}
          />
          <div className="fixed inset-y-0 right-0 z-[35] w-full sm:w-[500px] p-4 pt-[76px] sm:pt-[96px] shadow-2xl bg-[#F7F6F2] overflow-y-auto flex flex-col justify-start">
            {sidebarContent}
          </div>
        </div>
      )}
    </div>
  );
}
